import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Payment, AccountBalance, Store } from '@mui/icons-material';
import { InvoicePortalItem } from '../../entities/InvoicePortal';
import {
  AvailablePaymentMethods,
  PaymentMethodType,
} from '../../entities/PaymentMethods';

interface InvoiceCardProps {
  invoice: InvoicePortalItem;
  onPay: (invoiceId: number, paymentMethod?: PaymentMethodType) => void;
  onPaymentPromise: (invoiceId: number) => void;
  onShowPaymentOptions?: (invoice: InvoicePortalItem) => void;
  availablePaymentMethods?: AvailablePaymentMethods;
  isProcessingPayment?: boolean;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  onPay,
  onPaymentPromise,
  onShowPaymentOptions,
  availablePaymentMethods,
  isProcessingPayment = false,
}) => {
  const formatAmount = (amount: string, currency: string) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isOverdue = new Date(invoice.due_at) < new Date();

  // Calcular el monto pendiente (total - amount_paid)
  const pendingAmount =
    parseFloat(invoice.total) - parseFloat(invoice.amount_paid);

  // Función para obtener el badge según el status
  const getStatusBadge = (status: string) => {
    if (status === 'PARTIAL') {
      return (
        <Chip
          label="Parcialmente pagada"
          size="small"
          sx={{
            bgcolor: 'warning.main',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-label': {
              px: 1.5,
            },
          }}
        />
      );
    }
    if (status === 'PENDING') {
      return (
        <Chip
          label="Pendiente"
          size="small"
          sx={{
            bgcolor: 'info.main',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-label': {
              px: 1.5,
            },
          }}
        />
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 3,
        mb: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Layout responsive: columna en mobile, dos columnas en desktop */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 4 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          {/* Columna izquierda - Información de factura */}
          <Box sx={{ flex: 1 }}>
            {/* Título de factura */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 1.5,
                letterSpacing: '-0.01em',
                fontSize: { xs: '1.125rem', md: '1.25rem' },
              }}
            >
              {invoice.voucher_type_display} {invoice.number}
            </Typography>

            {/* Fecha de vencimiento con badge */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                Vence el {formatDate(invoice.due_at)}
              </Typography>
              {isOverdue && (
                <Chip
                  label="Vencida"
                  size="small"
                  sx={{
                    bgcolor: 'error.main',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-label': {
                      px: 1.5,
                    },
                  }}
                />
              )}
              {getStatusBadge(invoice.status)}
            </Box>

            {/* Detalles adicionales - CAE */}
            {invoice.cae && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    opacity: 0.8,
                    lineHeight: 1.4,
                    mb: 0.5,
                  }}
                >
                  CAE: {invoice.cae}
                </Typography>
                {invoice.cae_due && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      opacity: 0.8,
                      lineHeight: 1.4,
                    }}
                  >
                    Vto. CAE: {formatDate(invoice.cae_due)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Columna derecha - Monto y botones */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'stretch', md: 'center' },
              minWidth: { xs: 'auto', md: 280 },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            {/* Monto prominente */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                letterSpacing: '-0.025em',
                textAlign: { xs: 'left', md: 'center' },
                mb: { xs: 2.5, md: 3 },
                lineHeight: 1.1,
              }}
            >
              {formatAmount(pendingAmount.toString(), invoice.currency)}
            </Typography>

            {/* Botones de pago dinámicos */}
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              {/* Botón MercadoPago */}
              {availablePaymentMethods?.mercadopago?.available && (
                <Button
                  variant="contained"
                  onClick={() => onPay(invoice.id, 'mercadopago')}
                  disabled={isProcessingPayment}
                  sx={{
                    py: 2.5,
                    px: 4,
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    borderRadius: 2.5,
                    bgcolor: 'primary.main',
                    color: 'white',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    height: { xs: 52, md: 56 },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: isProcessingPayment ? 'none' : 'scale(0.98)',
                    },
                    '&:disabled': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      opacity: 0.7,
                    },
                    transition: 'all 0.2s ease',
                  }}
                  startIcon={
                    isProcessingPayment ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Payment />
                    )
                  }
                >
                  {isProcessingPayment
                    ? 'Procesando...'
                    : 'Pagar con MercadoPago'}
                </Button>
              )}

              {/* Botón Transferencia Bancaria */}
              {availablePaymentMethods?.bank_transfer && (
                <Button
                  variant="outlined"
                  onClick={() => onPay(invoice.id, 'bank_transfer')}
                  disabled={isProcessingPayment}
                  sx={{
                    py: 2,
                    px: 4,
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                    borderRadius: 2.5,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    height: { xs: 44, md: 48 },
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: 'primary.light',
                      color: 'white',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  startIcon={<AccountBalance />}
                >
                  Pagar con Transferencia Bancaria
                </Button>
              )}

              {/* Botón Pago Presencial */}
              {availablePaymentMethods?.cash_payment && (
                <Button
                  variant="outlined"
                  onClick={() => onPay(invoice.id, 'cash_payment')}
                  disabled={isProcessingPayment}
                  sx={{
                    py: 2,
                    px: 4,
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                    borderRadius: 2.5,
                    borderColor: 'secondary.main',
                    color: 'secondary.main',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    height: { xs: 44, md: 48 },
                    '&:hover': {
                      borderColor: 'secondary.dark',
                      bgcolor: 'secondary.light',
                      color: 'white',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  startIcon={<Store />}
                >
                  Pagar en Local
                </Button>
              )}

              {/* Fallback: Si no hay métodos disponibles o no se han cargado */}
              {!availablePaymentMethods && (
                <Button
                  variant="contained"
                  onClick={() => onPay(invoice.id)}
                  disabled={isProcessingPayment}
                  sx={{
                    py: 2.5,
                    px: 4,
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    borderRadius: 2.5,
                    bgcolor: 'primary.main',
                    color: 'white',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    height: { xs: 52, md: 56 },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: isProcessingPayment ? 'none' : 'scale(0.98)',
                    },
                    '&:disabled': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      opacity: 0.7,
                    },
                    transition: 'all 0.2s ease',
                  }}
                  startIcon={
                    isProcessingPayment ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Payment />
                    )
                  }
                >
                  {isProcessingPayment ? 'Procesando...' : 'Pagar'}
                </Button>
              )}

              {/* Botón secundario - Promesa de pago */}
              {/* <Button
                variant="outlined"
                onClick={() => onPaymentPromise(invoice.id)}
                sx={{
                  py: 2,
                  px: 4,
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  borderRadius: 2.5,
                  borderColor: 'rgba(0, 0, 0, 0.15)',
                  color: 'text.secondary',
                  textTransform: 'none',
                  letterSpacing: '-0.01em',
                  height: { xs: 44, md: 48 },
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Realizar promesa de pago
              </Button> */}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default InvoiceCard;
